import React from "react";
import {Grid} from "@mui/material";
import {INeighbourhood} from "../../api/Model";
import {SetState} from "../../util/FormHandlers";
import {filterSerializer} from "../../util/FilterSerializer";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {NeighbourhoodsFilter} from "../../components/filter-fields/NeighbourhoodsFilter";
import {StringEnumFilter} from "../../components/filter-fields/StringEnumFilter";
import {ResidenceFilter} from "../../components/filter-fields/ResidenceFilter";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import {matchResidence, Residence, ResidenceModel} from "../../api/ResidenceStore";
import {
    andFilter,
    applyFilter,
    filterChoice,
    filterNeighbourhoods,
    filterText,
    filterValue
} from "../../util/FilterUtil";


export interface EmployeeFilter {
    hasKey: '' | 'has-key' | 'one-key' | 'no-key' | 'many-keys';
    active: '' | 'active' | 'inactive';
    search: string;
    neighbourhoods: INeighbourhood[],
    residency: Residence | null,
}

const hasKeyOptions: { value: EmployeeFilter['hasKey'], label: string }[] = [
    {value: '', label: 'Any Number'},
    {value: 'has-key', label: 'One or More'},
    {value: 'no-key', label: 'No Key'},
    {value: 'one-key', label: 'Single Key'},
    {value: 'many-keys', label: 'Many Keys'},
];

const activeOptions: { value: EmployeeFilter['active'], label: string }[] = [
    {value: '', label: 'All'},
    {value: 'active', label: 'Active'},
    {value: 'inactive', label: 'Inactive'},
];

export const emptyEmployeeFilter: EmployeeFilter = {
    hasKey: '',
    active: '',
    search: '',
    neighbourhoods: [],
    residency: null,
}

export const employeeFilterSerializer = (
    residenceModel: ResidenceModel,
    allNeighbourhoods: INeighbourhood[],
) =>
    filterSerializer<EmployeeFilter>(
        {
            ...emptyEmployeeFilter,
            residency: residenceModel.residents,
        },
        b => {
            b.str('hasKey');
            b.str('active');
            b.str('search');
            b.neighbourhoods('neighbourhoods', allNeighbourhoods);
            b.residency('residency', residenceModel);
        });

export function filterEmployeeTableRows(tableRows: HierarchyEmployee[], filter: EmployeeFilter): HierarchyEmployee[] {
    return applyFilter(tableRows, andFilter(
        filterChoice(filter.hasKey, {
            '': undefined,
            'has-key': row => row.keys.length > 0,
            'no-key': row => row.keys.length <= 0,
            'one-key': row => row.keys.length === 1,
            'many-keys': row => row.keys.length > 1,
        }),
        filterChoice(filter.active, {
            '': undefined,
            active: row => !row.deactivated,
            inactive: row => row.deactivated,
        }),
        filterNeighbourhoods(filter.neighbourhoods, employee => employee.neighbourhoods),
        filterValue(
            matchResidence(filter.residency),
            employee => employee,
        ),
        filterText(filter.search, employee => [
            employee.fullName,
            ...(employee.teamPath || []).map(team => team.name),
            employee.jobTitle,
            ...(employee.neighbourhoods || []).map(neighbourhood => neighbourhood.name),
            ...employee.keys.flatMap(key => [
                key.key.keyNumber.toString(),
                key.historyRecord.vault.name,
                ...key.key.tags.map(tag => tag.name),
            ]),
        ]),
    ));
}

export function EmployeeTableFilter({filter, setFilter}: {
    filter: EmployeeFilter,
    setFilter: SetState<EmployeeFilter>,
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <ResidenceFilter filter={filter} setFilter={setFilter}/>
            <NeighbourhoodsFilter filter={filter} setFilter={setFilter}/>
            <StringEnumFilter label="Active Status" filter={filter} setFilter={setFilter} property="active"
                              options={activeOptions}/>
            <StringEnumFilter label="Owned Keys" filter={filter} setFilter={setFilter} property="hasKey"
                              options={hasKeyOptions}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyEmployeeFilter} setFilter={setFilter}/>
        </Grid>
    );
}
